// -----------------------------------------------------------------------------
// prepTasks.js
// Build a data-driven "preparation checklist" for a single degree, anchored on
// its own `application` block plus subject-level requirements (english_test,
// entrance_test, portfolio_required, interview_required, international.*).
//
// Every task carries a *targeted* ISO date so the tracker can render a
// countdown chip identical to the existing deadline rows. The dates are
// offsets from either the application deadline (`closes`) or a later
// milestone — we deliberately DO NOT invent institution-specific test-window
// dates we don't have; instead we surface "by this date" so the applicant
// still has a concrete anchor.
// -----------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

function isoAddDays(iso, deltaDays) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const dt = new Date(`${m[1]}-${m[2]}-${m[3]}T23:59:59Z`);
  if (isNaN(dt.getTime())) return null;
  const shifted = new Date(dt.getTime() + deltaDays * DAY_MS);
  return shifted.toISOString().slice(0, 10);
}

function firstIsoIn(str) {
  // "2027-03-14 (Parcoursup) / 2027-01-15 (DAP)" → "2027-03-14"
  if (!str || typeof str !== "string") return null;
  const m = str.match(/(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

// Human-friendly platform-specific copy for the "register" task.
function platformCopy(platform) {
  const p = (platform || "").toLowerCase();
  if (p.includes("ucas"))
    return {
      label: "Register on UCAS",
      description:
        "Create your UCAS Hub account, add your school as a linked centre, and start the application shell so references can be attached later.",
    };
  if (p.includes("common"))
    return {
      label: "Create a Common App account",
      description:
        "Start your Common App profile, add this university, and connect your school so counsellors can upload documents.",
    };
  if (p.includes("coalition"))
    return {
      label: "Start your Coalition application",
      description:
        "Open the Coalition portal, add the university, and connect your school profile.",
    };
  if (p.includes("uni-assist"))
    return {
      label: "Register with uni-assist",
      description:
        "Create a uni-assist account and start the pre-check for your certificates — turnaround is 4–6 weeks.",
    };
  if (p.includes("parcoursup"))
    return {
      label: "Register on Parcoursup",
      description:
        "Create your Parcoursup profile and add this programme to your wish-list within the platform's short January window.",
    };
  if (p.includes("studielink"))
    return {
      label: "Register on Studielink",
      description:
        "Open your Studielink account and add this programme — you'll need DigiD or a Studielink login code.",
    };
  return {
    label: `Open the ${platform || "programme"} application`,
    description:
      "Register on the programme's own application portal — the link is on the university's admissions page.",
  };
}

// -----------------------------------------------------------------------------
// Main builder
// -----------------------------------------------------------------------------
export function buildPrepTasks(degree) {
  if (!degree || !degree.application) return [];
  const app = degree.application || {};
  const closes = firstIsoIn(app.closes);
  const opens = firstIsoIn(app.opens);
  const decisionBy = firstIsoIn(app.decision_by);
  const depositBy = firstIsoIn(app.deposit_deadline);

  const tasks = [];

  // ---- 1. Register on platform -------------------------------------------
  if (app.platform) {
    const cp = platformCopy(app.platform);
    tasks.push({
      key: "register",
      phase: "Before application",
      label: cp.label,
      description: cp.description,
      // Aim to be registered by the day applications open, or (if we don't
      // have an `opens` date) 12 weeks before the deadline.
      target_iso: opens || (closes ? isoAddDays(closes, -84) : null),
      target_kind: opens ? "opens" : "recommended",
    });
  }

  // ---- 2. English test (international) -----------------------------------
  const eng = degree.english_test || {};
  if (eng.ielts_min || eng.toefl_min || eng.duolingo_min) {
    const parts = [];
    if (eng.ielts_min) parts.push(`IELTS ${eng.ielts_min}`);
    if (eng.toefl_min) parts.push(`TOEFL ${eng.toefl_min}`);
    if (eng.duolingo_min) parts.push(`Duolingo ${eng.duolingo_min}`);
    tasks.push({
      key: "english_test",
      phase: "Before application",
      label: `Sit your English test (${parts.join(" or ")})`,
      description:
        "Book and sit an accepted English test — results take 2–3 weeks. Take it early so you can retest if needed. Only required if English is not your first language / medium of instruction.",
      target_iso: closes ? isoAddDays(closes, -84) : null,
      target_kind: "recommended",
      international_only: true,
    });
  }

  // ---- 3. Entrance test -------------------------------------------------
  if (degree.entrance_test) {
    tasks.push({
      key: "entrance_test",
      phase: "Before application",
      label: `Register for ${degree.entrance_test}`,
      description: `${degree.entrance_test} has its own registration window — check the test's official site for exact dates. Give yourself 4+ weeks to prepare with past papers.`,
      target_iso: closes ? isoAddDays(closes, -28) : null,
      target_kind: "recommended",
    });
  }

  // ---- 4. Portfolio -----------------------------------------------------
  if (degree.portfolio_required) {
    tasks.push({
      key: "portfolio",
      phase: "Before application",
      label: "Prepare your portfolio",
      description:
        "Curate 10–20 pieces that show range, process, and finish. Read the university's portfolio brief for size, medium, and submission format before you start.",
      target_iso: closes ? isoAddDays(closes, -56) : null,
      target_kind: "recommended",
    });
  }

  // ---- 5. Academic references -------------------------------------------
  const refN = Number(app.references_required || 0);
  if (refN > 0) {
    tasks.push({
      key: "references",
      phase: "Before application",
      label: `Ask ${refN} referee${refN > 1 ? "s" : ""} for a reference`,
      description:
        "Approach a teacher who's taught you recently and knows your work in a relevant subject. Give them at least 6 weeks and share your personal-statement draft plus your target course.",
      target_iso: closes ? isoAddDays(closes, -56) : null,
      target_kind: "recommended",
    });
  }

  // ---- 6. Personal statement --------------------------------------------
  if (app.personal_statement_required) {
    tasks.push({
      key: "personal_statement_draft",
      phase: "Before application",
      label: "Draft your personal statement",
      description:
        "Draft 1: 600 words, focused on why THIS subject. Iterate 2–3 times with a teacher's feedback. Avoid clichés and quoting famous people — admissions tutors want your reading, not theirs.",
      target_iso: closes ? isoAddDays(closes, -84) : null,
      target_kind: "recommended",
    });
    tasks.push({
      key: "personal_statement_final",
      phase: "Application week",
      label: "Finalise personal statement",
      description:
        "Final proofread — check character/word count against the platform's limit and paste it into the application form early to catch any encoding issues.",
      target_iso: closes ? isoAddDays(closes, -14) : null,
      target_kind: "recommended",
    });
  }

  // ---- 7. Supplementary essays ------------------------------------------
  const supN = Number(app.supplementary_essays || 0);
  if (supN > 0) {
    tasks.push({
      key: "supplementary_essays",
      phase: "Before application",
      label: `Write ${supN} supplementary essay${supN > 1 ? "s" : ""}`,
      description:
        "Each 'why us?' essay should reference specific professors, courses, or labs. Generic answers hurt more than they help — admissions officers read hundreds a day.",
      target_iso: closes ? isoAddDays(closes, -42) : null,
      target_kind: "recommended",
    });
  }

  // ---- 8. Application fee (only if non-trivial) -------------------------
  const fee = Number(app.application_fee_usd || 0);
  if (fee > 0) {
    tasks.push({
      key: "application_fee",
      phase: "Application week",
      label: `Pay the application fee (${fee > 0 ? `$${fee}` : ""})`,
      description:
        "Have a card or payment method ready. If you're on a low-income fee-waiver programme (Common App, UCAS), request the waiver a week before you submit.",
      target_iso: closes ? isoAddDays(closes, -7) : null,
      target_kind: "recommended",
    });
  }

  // ---- 9. Submit --------------------------------------------------------
  if (closes) {
    tasks.push({
      key: "submit",
      phase: "Application week",
      label: "Submit application",
      description:
        "Don't wait for the last hour — portals slow down under load. Aim to submit 24–48 hours before the official close.",
      target_iso: closes,
      target_kind: "deadline",
    });
  }

  // ---- 10. Interview prep -----------------------------------------------
  if (degree.interview_required) {
    tasks.push({
      key: "interview",
      phase: "After application",
      label: "Prepare for interview",
      description:
        "Re-read your personal statement, revise the reading list on the course page, and rehearse 3–5 subject questions out loud. Book a mock with a teacher.",
      // Interviews are typically invited within a few weeks of closing.
      target_iso: closes ? isoAddDays(closes, 21) : null,
      target_kind: "recommended",
    });
  }

  // ---- 11. Firm/insurance decision --------------------------------------
  if (decisionBy) {
    tasks.push({
      key: "firm_insurance",
      phase: "After application",
      label: "Decide firm & insurance choices",
      description:
        "Once you have offers, pick one 'firm' (your top choice) and one 'insurance' (a safer offer). Compare cost after aid, entry-grade risk, and career fit — not just brand.",
      target_iso: isoAddDays(decisionBy, 28),
      target_kind: "recommended",
    });
  }

  // ---- 12. Deposit ------------------------------------------------------
  if (depositBy) {
    tasks.push({
      key: "deposit",
      phase: "After application",
      label: "Pay tuition/enrolment deposit",
      description:
        "Missing the deposit day almost always releases your seat. If you're waiting on scholarship confirmation, email the admissions office in advance — most will grant a short extension in writing.",
      target_iso: depositBy,
      target_kind: "deadline",
    });
  }

  // ---- 13. Visa (international only) ------------------------------------
  const intl = degree.international || {};
  if (intl.visa_eligible !== false) {
    // Aim to lodge the visa 8 weeks before term start; if we don't know term
    // start, anchor 12 weeks after the deposit deadline.
    const target =
      depositBy ? isoAddDays(depositBy, 30) :
      decisionBy ? isoAddDays(decisionBy, 60) :
      closes ? isoAddDays(closes, 180) : null;
    tasks.push({
      key: "visa",
      phase: "After application",
      label: "Apply for your student visa",
      description:
        "You'll need your CAS/I-20 (issued after deposit), financial evidence covering tuition + living costs, and — for the UK — a TB test if you're from a listed country. Lodge 8 weeks before term to be safe.",
      target_iso: target,
      target_kind: "recommended",
      international_only: true,
    });
  }

  // Chronological order for the checklist UI.
  return tasks.sort((a, b) => {
    if (!a.target_iso) return 1;
    if (!b.target_iso) return -1;
    return a.target_iso < b.target_iso ? -1 : a.target_iso > b.target_iso ? 1 : 0;
  });
}

// Helper reused by the UI to compute days-until for a task's target date.
export function taskDaysUntil(iso) {
  if (!iso) return null;
  const m = iso.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const dt = new Date(`${m[1]}-${m[2]}-${m[3]}T23:59:59Z`);
  if (isNaN(dt.getTime())) return null;
  const ms = dt.getTime() - Date.now();
  return Math.round(ms / DAY_MS);
}
